// Warstwa synchronizacji danych „localStorage-only" z backendem (AppSettings).
//
// Cel: dane domenowe trzymane dotąd wyłącznie w przeglądarce (struktura
// organizacyjna, uprawnienia, słowniki, profil firmy, dziennik ZOiS, atrybuty
// klasyfikacji, log prób e-mail) zyskują trwałość w DB i — dla scope "shared" —
// współdzielenie między pracownikami kancelarii.
//
// Zasada działania (pełna wsteczna kompatybilność, zero zmian w call-site'ach):
//   • localStorage pozostaje żywym, synchronicznym cache — komponenty czytają jak dotąd;
//   • każdy zapis zarejestrowanego klucza jest mirrorowany do BE (debounce);
//   • przy starcie aplikacji (po zalogowaniu) hydratujemy localStorage z BE,
//     a jeśli BE jest pusty a localStorage ma dane — wykonujemy jednorazową migrację LS→BE.

import {
  AppSettingScope,
  getAppSettingsBulk,
  setAppSetting,
} from "api/appSettingsService";

// ── Rejestr: klucz localStorage → scope ─────────────────────────────────────
// shared = współdzielone w kancelarii, user = prywatne użytkownika.
const REGISTRY: Record<string, AppSettingScope> = {
  // 1. Struktura organizacyjna i kadry (współdzielone)
  org_chart_v5: "shared",
  team_structure_v1: "shared",
  team_structure_assignments_v1: "shared",
  team_privileges_v1: "shared",
  job_grades_custom_v1: "shared",

  // 2. Dziennik księgowań / kontrahenci ZOiS (współdzielone)
  zois_journals_rows: "shared",
  zois_contractors_rows: "shared",
  zois_journals_file: "shared",
  zois_contractors_file: "shared",
  contractors_nip_overrides_v1: "shared",
  contractors_merges_v1: "shared",
  zois_migration_system: "shared",

  // 3. Słowniki (współdzielone)
  jrwa_tree_v2: "shared",
  kadry_stopnie_v1: "shared",
  kadry_stanowiska_v1: "shared",
  wpk_gminy_tree_v1: "shared",
  wpk_user_plans_v1: "shared",
  wpk_posting_plan_v1: "shared",
  mpp_tree_v1: "shared",

  // 4. Profil firmy (współdzielony)
  biz_profile_local: "shared",

  // 5. Atrybuty klasyfikacji niesynchronizowane przez /ui/status-display (prywatne)
  tag_tooltips_v1: "user",
  tag_order_v1: "user",
  status_order_v1: "user",
  status_tooltips_v1: "user",
  // Wygląd pól tekstowych (Ustawienia → Widok → Wygląd pól): utils/inputAppearance.
  input_appearance_v1: "user",

  // 6. Integracje — log prób e-mail (prywatne)
  email_integration_attempts_v1: "user",

  // 7. Filtry wlasne rejestru wydatkow budzetu (components/SavedFilters, prywatne)
  expensesRegister_filters: "user",
};

const DEBOUNCE_MS = 800;

let installed = false;
let suspendMirror = false; // true podczas zapisu hydratacyjnego (nie odsyłaj z powrotem)
const timers: Record<string, ReturnType<typeof setTimeout>> = {};

let originalSetItem: ((k: string, v: string) => void) | null = null;
let originalRemoveItem: ((k: string) => void) | null = null;

const schedulePush = (key: string, value: string | null) => {
  const scope = REGISTRY[key];
  if (!scope) return;
  if (timers[key]) clearTimeout(timers[key]);
  timers[key] = setTimeout(() => {
    setAppSetting(scope, key, value).catch(() => {
      /* offline / brak auth — localStorage pozostaje źródłem; spróbujemy przy następnym zapisie */
    });
  }, DEBOUNCE_MS);
};

/** Zapis do localStorage z pominięciem mirrora (używany przy hydratacji z BE). */
const silentSet = (key: string, value: string) => {
  suspendMirror = true;
  try {
    (originalSetItem ?? window.localStorage.setItem.bind(window.localStorage))(key, value);
  } finally {
    suspendMirror = false;
  }
};

/**
 * Instaluje mirror: każdy zapis/usunięcie zarejestrowanego klucza localStorage
 * jest (debounce) odsyłany do BE. Wołać raz, po zalogowaniu.
 */
export const installAppSettingsMirror = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const ls = window.localStorage;
  originalSetItem = ls.setItem.bind(ls);
  originalRemoveItem = ls.removeItem.bind(ls);

  ls.setItem = (key: string, value: string) => {
    originalSetItem!(key, value);
    if (!suspendMirror && REGISTRY[key]) schedulePush(key, value);
  };
  ls.removeItem = (key: string) => {
    originalRemoveItem!(key);
    if (!suspendMirror && REGISTRY[key]) schedulePush(key, null);
  };
};

const applyHydration = (
  scope: AppSettingScope,
  beValues: Record<string, string | null>,
): string[] => {
  const changed: string[] = [];
  for (const key of Object.keys(REGISTRY)) {
    if (REGISTRY[key] !== scope) continue;
    const beVal = beValues[key] ?? null;
    const lsVal = window.localStorage.getItem(key);

    if (beVal != null) {
      // BE jest źródłem prawdy — nadpisz cache, jeśli się różni.
      if (beVal !== lsVal) {
        silentSet(key, beVal);
        changed.push(key);
      }
    } else if (lsVal != null) {
      // BE pusty, lokalnie coś jest → jednorazowa migracja LS→BE.
      setAppSetting(scope, key, lsVal).catch(() => { /* spróbujemy później */ });
    }
  }
  return changed;
};

/**
 * Hydratacja localStorage z BE, wołana raz po zalogowaniu (App.tsx).
 *
 * Klucze SHARED: pull BE→LS + CZYSZCZENIE lokalnych kluczy, których BE bieżącego
 * profilu nie ma (clear-on-null). NIE migrujemy shared LS→BE — localStorage jest
 * globalny per-origin, więc migracja przenosiłaby dane innego profilu/użytkownika
 * do bieżącego (udowodniony wciek: te same pliki na wielu profilach). Nowe zapisy
 * shared trafiają do BE przez mirror (patched setItem), nie przez migrację.
 * Klucze USER: per-użytkownik → migrujemy LS→BE dla pustych (prefy nie wyciekają
 * między profilami tego samego usera).
 */
export const hydrateAppSettings = async (): Promise<void> => {
  const keys = Object.keys(REGISTRY);
  const sharedKeys = keys.filter((k) => REGISTRY[k] === "shared");
  const userKeys = keys.filter((k) => REGISTRY[k] === "user");

  try {
    const [sharedVals, userVals] = await Promise.all([
      getAppSettingsBulk("shared", sharedKeys),
      getAppSettingsBulk("user", userKeys),
    ]);

    const changed = [
      ...applyHydration("shared", sharedVals),
      ...applyHydration("user", userVals),
    ];

    if (changed.length > 0 && typeof window !== "undefined") {
      // Komponenty, które chcą reagować na świeże dane z BE, mogą nasłuchiwać.
      window.dispatchEvent(new CustomEvent("app-settings-hydrated", { detail: { keys: changed } }));
    }
  } catch {
    /* brak sieci / sesji — zostaje localStorage; mirror dośle zmiany później */
  }
};
